import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

const menuItems = [
  { path: '/', icon: 'ri-dashboard-line', label: 'Dashboard', activeIcon: 'ri-dashboard-fill' },
  { path: '/fizbo', icon: 'ri-map-pin-line', label: 'FİZBO Takip', activeIcon: 'ri-map-pin-fill' },
  { path: '/fizbo-kanban', icon: 'ri-kanban-view', label: 'Kanban Pano', activeIcon: 'ri-kanban-view' },
  { path: '/portfoy', icon: 'ri-building-line', label: 'Portföy', activeIcon: 'ri-building-fill' },
  { path: '/talep-bul', icon: 'ri-search-line', label: 'Talep Bul', activeIcon: 'ri-search-fill' },
  { path: '/musteri-rehber', icon: 'ri-user-line', label: 'Müşteri', activeIcon: 'ri-user-fill' },
  { path: '/toplu-mesaj', icon: 'ri-message-3-line', label: 'Toplu Mesaj', activeIcon: 'ri-message-3-fill' },
  { path: '/hatirlatmalar', icon: 'ri-time-line', label: 'Hatırlatıcılar', activeIcon: 'ri-time-fill' },
  { path: '/raporlar', icon: 'ri-bar-chart-line', label: 'Raporlar', activeIcon: 'ri-bar-chart-fill' },
  { path: '/ayarlar', icon: 'ri-settings-3-line', label: 'Ayarlar', activeIcon: 'ri-settings-3-fill' },
];

function getInitials(fullName: string | null | undefined, email: string | null | undefined): string {
  if (fullName) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  return email ? email.slice(0, 2).toUpperCase() : 'U';
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { isDark } = useTheme();
  const { epUser } = useAuth();
  const location = useLocation();

  const displayName = epUser?.full_name || epUser?.email?.split('@')[0] || 'Kullanıcı';
  const initials = getInitials(epUser?.full_name, epUser?.email);

  return (
    <aside
      className={`fixed left-0 top-0 h-full z-40 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      } ${isDark ? 'bg-dark-card border-r border-dark-border' : 'bg-white border-r border-light-border'}`}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-opacity-10">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/8d501578-6f11-4960-92e0-74720553c4c2.png"
                alt="Logo"
                className="w-8 h-8 object-contain"
              />
              <span className={`font-bold text-lg ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                EmlakPro
              </span>
            </div>
          )}
          {collapsed && (
            <img
              src="https://public.readdy.ai/ai/img_res/8d501578-6f11-4960-92e0-74720553c4c2.png"
              alt="Logo"
              className="w-8 h-8 object-contain mx-auto"
            />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
            }`}
          >
            <i className={collapsed ? 'ri-arrow-right-s-line text-lg' : 'ri-arrow-left-s-line text-lg'} />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                  isActive
                    ? 'bg-primary-500 text-white'
                    : isDark
                    ? 'text-dark-muted hover:bg-dark-border hover:text-dark-text'
                    : 'text-light-muted hover:bg-light-border hover:text-light-text'
                }`}
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className={`${isActive ? item.activeIcon : item.icon} text-lg`} />
                </div>
                {!collapsed && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className={`p-3 border-t ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{initials}</span>
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  {displayName}
                </p>
                <p className={`text-xs truncate ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {epUser?.role || 'Danışman'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}